import {
  Match,
  Show,
  Switch,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { Portal } from "solid-js/web";

import { Trans } from "@lingui/solid/macro";
import { PublicChannelInvite } from "stoat.js";
import { css, cva } from "styled-system/css";
import { styled } from "styled-system/jsx";

import { IS_DEV, useClient } from "@revolt/client";
import { useInstance } from "@revolt/instance";
import { useModals } from "@revolt/modal";
import { useNavigate } from "@revolt/routing";
import {
  Button,
  CategoryButton,
  Column,
  Header,
  iconSize,
  main,
} from "@revolt/ui";

import MdExplore from "@material-design-icons/svg/filled/explore.svg?component-solid";
import MdGroups3 from "@material-design-icons/svg/filled/groups_3.svg?component-solid";
import MdHome from "@material-design-icons/svg/filled/home.svg?component-solid";
import MdSettings from "@material-design-icons/svg/filled/settings.svg?component-solid";

import Wordmark from "../../public/assets/web/wordmark.svg?component-solid";

import { HeaderIcon } from "./common/CommonHeader";
import { Dashboard } from "./Dashboard";

/**
 * Base layout of the home page (i.e. the header/background)
 */
const Base = styled("div", {
  base: {
    width: "100%",
    display: "flex",
    flexDirection: "column",

    color: "var(--md-sys-color-on-surface)",
  },
});

const SCROLL_HINT_CSS =
  "@keyframes mj-dot{0%{transform:translateY(0);opacity:0}25%{opacity:1}75%{opacity:1}100%{transform:translateY(18px);opacity:0}}" +
  "@media (prefers-reduced-motion:reduce){.mj-dot{animation:none!important;opacity:1!important}}";

/**
 * Gentle "scroll down" hint that fades in after a few seconds
 * if there is still content below the fold and the user has not scrolled.
 */
function ScrollHint() {
  const [visible, setVisible] = createSignal(false);
  let shown = 0;
  let timer: number | undefined;

  // 4s the first time, then 8s, 11s, 14s ... up to 20s
  const delayFor = (n: number) =>
    n === 0 ? 4000 : Math.min(8000 + (n - 1) * 3000, 20000);

  const check = () => {
    const end = document.getElementById("majalis-home-end");
    if (end && end.getBoundingClientRect().bottom > window.innerHeight + 8) {
      shown += 1;
      setVisible(true);
    } else {
      // nothing below the fold right now, look again later
      timer = window.setTimeout(check, 4000);
    }
  };

  const restart = () => {
    setVisible(false);
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(check, delayFor(shown));
  };

  const EVENTS = ["scroll", "wheel", "touchmove", "keydown", "pointerdown"];

  onMount(() => {
    timer = window.setTimeout(check, delayFor(0));
    EVENTS.forEach((e) =>
      window.addEventListener(e, restart, { capture: true, passive: true }),
    );
  });

  onCleanup(() => {
    if (timer) window.clearTimeout(timer);
    EVENTS.forEach((e) => window.removeEventListener(e, restart, true));
  });

  return (
    <Portal>
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        "padding-top": "80px",
        "padding-bottom": "24px",
        background:
          "linear-gradient(to top, rgba(0, 0, 0, 0.88) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 100%)",
        "z-index": 5,
        display: "flex",
        "flex-direction": "column",
        "align-items": "center",
        gap: "10px",
        "pointer-events": "none",
        opacity: visible() ? 1 : 0,
        transform: visible() ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 0.9s ease, transform 0.9s ease",
      }}
    >
      <style>{SCROLL_HINT_CSS}</style>
      <span
        style={{
          "font-size": "13px",
          "font-weight": 600,
          "letter-spacing": "0.3em",
          "padding-left": "0.3em",
          "text-transform": "uppercase",
          color: "#FFC62E",
          "text-shadow": "0 1px 8px rgba(0, 0, 0, 0.7)",
        }}
      >
        <Trans>Scroll</Trans>
      </span>
      <div
        style={{
          position: "relative",
          width: "28px",
          height: "46px",
          "border-radius": "12px",
          border: "2px solid #FFC62E",
          "box-shadow": "0 0 20px rgba(255, 198, 46, 0.55)",
        }}
      >
        <div
          class="mj-dot"
          style={{
            position: "absolute",
            top: "10px",
            left: "50%",
            width: "6px",
            height: "6px",
            "margin-left": "-3px",
            "border-radius": "50%",
            background: "#FFC62E",
            animation: "mj-dot 1.9s ease-in-out infinite",
          }}
        />
      </div>
    </div>
    </Portal>
  );
}

/**
 * Layout of the content as a whole
 */
const content = cva({
  base: {
    ...main.raw(),

    padding: "0",
    backgroundImage:
      "radial-gradient(700px 380px at 50% 0%, rgba(201, 162, 75, 0.18), transparent 70%)",

    gap: "32px",
    alignItems: "center",
    justifyContent: "flex-start",
    "& > :first-child": { marginTop: "auto" },
    "& > :last-child": { marginBottom: "auto" },
  },
});

/**
 * Layout of the buttons
 */
const Buttons = styled("div", {
  base: {
    gap: "8px",
    padding: "8px",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    borderRadius: "var(--borderRadius-lg)",

    color: "var(--md-sys-color-on-surface-variant)",
    background: "var(--md-sys-color-surface-variant)",
  },
});

/**
 * Make sure the columns are separated
 */
const SeparatedColumn = styled(Column, {
  base: {
    justifyContent: "stretch",
    marginInline: "0.25em",
    width: "260px",
    "& > *": {
      flexGrow: 1,
    },
  },
});

/**
 * Welcome text for new members
 */
const Welcome = styled("div", {
  base: {
    width: "min(100%, 680px)",
    padding: "0 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
    textAlign: "center",

    "& h2": {
      margin: 0,
      fontSize: "2.6rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    "& .tagline": {
      margin: 0,
      lineHeight: 1.6,
      fontSize: "1.1rem",
      maxWidth: "34em",
      color: "var(--md-sys-color-on-surface-variant)",
    },
    "& .divider": {
      display: "flex",
      alignItems: "center",
      gap: "14px",
      width: "min(100%, 420px)",
      margin: "18px 0 6px",
      color: "var(--md-sys-color-primary)",
    },
    "& .divider::before, & .divider::after": {
      content: '""',
      flex: 1,
      height: "1px",
      background:
        "linear-gradient(90deg, transparent, var(--md-sys-color-primary), transparent)",
    },
    "& h3": {
      margin: "6px 0 4px",
      fontSize: "0.8rem",
      letterSpacing: "0.2em",
      textTransform: "uppercase",
      fontWeight: 600,
      color: "var(--md-sys-color-primary)",
    },
    "& ol": {
      margin: 0,
      padding: 0,
      listStyle: "none",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      textAlign: "start",
      counterReset: "step",
    },
    "& li": {
      counterIncrement: "step",
      display: "flex",
      alignItems: "center",
      gap: "16px",
      padding: "16px 20px",
      lineHeight: 1.5,
      borderRadius: "14px",
      background: "var(--md-sys-color-surface-container-low)",
      border: "1px solid var(--md-sys-color-outline-variant)",
      borderInlineStart: "3px solid var(--md-sys-color-primary)",
      boxShadow: "0 6px 18px rgba(0, 0, 0, 0.35)",
    },
    "& .hint": {
      margin: 0,
      padding: "12px 16px",
      lineHeight: 1.5,
      borderRadius: "12px",
      color: "var(--md-sys-color-on-surface-variant)",
      background: "var(--md-sys-color-surface-container-low)",
      border: "1px dashed var(--md-sys-color-primary)",
    },
    "& li[data-link]": {
      cursor: "pointer",
      transition: "var(--transitions-fast) all",
    },
    "& li[data-link]:hover": {
      background: "var(--md-sys-color-surface-container)",
      borderColor: "var(--md-sys-color-primary)",
    },
    "& li[data-link]::after": {
      content: '"\\203A"',
      marginInlineStart: "auto",
      fontSize: "1.6rem",
      lineHeight: 1,
      color: "var(--md-sys-color-primary)",
    },
    "& li::before": {
      content: "counter(step)",
      flex: "none",
      width: "34px",
      height: "34px",
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      fontWeight: 700,
      background: "linear-gradient(135deg, #FFC62E, #F29D0A)",
      color: "#1A1200",
      boxShadow: "0 0 14px rgba(255, 184, 28, 0.45)",
    },
  },
});

// Majalis: keep the browser's install prompt so the Welcome step can open it
let deferredInstallPrompt: any = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
  });
}

/**
 * Home page
 */
export function HomePage() {
  const { openModal } = useModals();
  const navigate = useNavigate();
  const client = useClient();
  const instance = useInstance();

  // Majalis: open the first channel (in any of the user's servers) whose name matches
  const openChannel = (pattern: RegExp) => {
    for (const server of client()!.servers.toList()) {
      const channel = server.channels.find((c) => pattern.test(c.name ?? ""));
      if (channel) {
        navigate(`/server/${channel.serverId}/channel/${channel.id}`);
        return;
      }
    }
  };
  const [installHint, setInstallHint] = createSignal("");
  const installApp = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      await deferredInstallPrompt.userChoice;
      deferredInstallPrompt = null;
      setInstallHint("");
    } else if (/iphone|ipad|ipod/i.test(navigator.userAgent)) {
      setInstallHint(
        "On iPhone or iPad: tap the Share button in Safari, then choose Add to Home Screen.",
      );
    } else {
      setInstallHint(
        "Open your browser menu and choose Install app or Add to Home Screen.",
      );
    }
  };
  const installProps = {
    "data-link": "",
    role: "link" as const,
    tabIndex: 0,
    onClick: installApp,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === "Enter") installApp();
    },
  };
  const linkProps = (pattern: RegExp) => ({
    "data-link": "",
    role: "link" as const,
    tabIndex: 0,
    onClick: () => openChannel(pattern),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === "Enter") openChannel(pattern);
    },
  });

  // check if we're stoat.chat; if so, check if the user is in the Lounge
  const showLoungeButton = instance.isStoat;
  const isInLounge =
    client()!.servers.get("01F7ZSBSFHQ8TA81725KQCSDDP") !== undefined;

  return (
    <Base>
      <Header placement="primary">
        <HeaderIcon>
          <MdHome {...iconSize(22)} />
        </HeaderIcon>
        <Trans>Home</Trans>
      </Header>
      <div use:scrollable={{ class: content() }}>
        <Dashboard />
        <Show when={false}>
        <Column>
          <Wordmark
            class={css({
              width: "150px",
              fill: "var(--md-sys-color-on-surface)",
            })}
          />
        </Column>
        <Welcome>
          <h2>
            <Trans>Welcome to Majalis</Trans>
          </h2>
          <p class="tagline">
            <Trans>
              Learn from educators. Grow in knowledge. Strengthen the Ummah for
              the sake of Allah.
            </Trans>
          </p>
          <div class="divider">
            <span aria-hidden="true">✦</span>
          </div>
          <h3>
            <Trans>Getting started</Trans>
          </h3>
          <ol>
            <li {...installProps}>
              <Trans>
                Install Majalis on your phone to get seminar reminders.
              </Trans>
            </li>
            <li {...linkProps(/introduce/i)}>
              <Trans>Join the room for your topic and introduce yourself.</Trans>
            </li>
            <li {...linkProps(/schedule/i)}>
              <Trans>Find upcoming seminars in the schedule channel.</Trans>
            </li>
            <li {...linkProps(/^rules$/i)}>
              <Trans>Read the community guidelines before you post.</Trans>
            </li>
            <li {...linkProps(/question/i)}>
              <Trans>
                To message a member privately, send a friend request. Questions
                for educators go in the Q&amp;A channels.
              </Trans>
            </li>
          </ol>
          <Show when={installHint()}>
            <p class="hint">{installHint()}</p>
          </Show>
          <div
            id="majalis-home-end"
            aria-hidden="true"
            style={{ height: "72px", "flex-shrink": 0 }}
          />
          <ScrollHint />
        </Welcome>
        </Show>
      </div>
    </Base>
  );
}
